// src/ConsumerApp/Screens/Home/components/FeaturedCategoriesSection.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowRight from '../../../../assets/icons/ArrowRight.svg';
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import ProductCard from '../../ProductsList/components/ProductCard';
import ProductOptionsModal from '../../ProductDetails/components/ProductOptionsModal';
import { theme } from '../../../../assets/theme/theme';

// Shared API Hooks
import {
  useGetBucketSummary,
  useGetProductVariants,
} from '../../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../../api/savedList/savedList.hooks';
import { useGetDefaultDeliveryAddress } from '../../../api/projects/project.hooks';
import { guestBucketManager } from '../../../utils/guestBucketManager';

// Individual Row Sub-Component with Dedicated Scroll Observers
function CategoryRail({
  section,
  customerId,
  hubId,
  bucketItemsMap,
  savedItemsSet,
  getProductQuantity,
  handleToggleSave,
  handleOpenOptions,
}) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const {
    product_category_id,
    product_category_name,
    products = [],
  } = section;

  // Measure and calculate whether arrows are necessary
  const checkScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;

    const hasOverflow = maxScroll > 5;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft >= maxScroll - 5;

    setCanScrollLeft(!isAtStart && hasOverflow);
    setCanScrollRight(!isAtEnd && hasOverflow);
  }, []);

  useEffect(() => {
    checkScrollState();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', checkScrollState, { passive: true });
    window.addEventListener('resize', checkScrollState);

    const observer = new ResizeObserver(checkScrollState);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', checkScrollState);
      window.removeEventListener('resize', checkScrollState);
      observer.disconnect();
    };
  }, [checkScrollState, products]);

  const handleScroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollAmount = Math.max(el.clientWidth * 0.75, 200);
    const targetScroll =
      direction === 'left'
        ? el.scrollLeft - scrollAmount
        : el.scrollLeft + scrollAmount;

    el.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  const handleNavigateToCategory = () => {
    navigate('/consumer/productslist', {
      state: {
        categoryId: product_category_id,
        categoryName: product_category_name,
      },
    });
  };

  return (
    <section style={{ width: '100%', position: 'relative' }}>
      {/* Header: Left Category Name + Desktop-Only Right "See All" */}
      <div className="bb-cat-section-header">
        <div className="bb-cat-title-cluster" onClick={handleNavigateToCategory}>
          <h3 className="bb-cat-heading">{product_category_name}</h3>
        </div>

        <div
          className="bb-cat-see-all-btn desktop-only"
          onClick={handleNavigateToCategory}
          role="button"
          tabIndex={0}
        >
          <span className="bb-cat-see-all-text">See All</span>
          <img src={ArrowRight} alt="arrow" style={{ width: 12, height: 12 }} />
        </div>
      </div>

      {/* Rail Container Wrapper */}
      <div className="bb-rail-outer-shell">
        {canScrollLeft && (
          <button
            type="button"
            className="bb-scroll-nav-btn bb-scroll-nav-left"
            onClick={() => handleScroll('left')}
            aria-label="Scroll backward"
          >
            <img
              src={CaretRight}
              alt="‹"
              style={{
                width: 14,
                height: 14,
                transform: 'rotate(180deg)',
              }}
            />
          </button>
        )}

        {/* Horizontal Product Rail */}
        <div ref={scrollRef} className="featured-products-scroll">
          {products.map((prod) => {
            const pid = String(prod.product_id || prod._id);
            const quantity = getProductQuantity(pid);
            const bucketEntry = customerId ? bucketItemsMap.get(pid) : null;

            return (
              <div key={pid} className="featured-card-wrapper">
                <ProductCard
                  item={prod}
                  customerId={customerId}
                  hubId={hubId}
                  savedBucketQuantity={quantity}
                  bucketId={bucketEntry?.bucketId}
                  isSavedItem={savedItemsSet.has(pid)}
                  onToggleSave={handleToggleSave}
                  onOptionsClick={handleOpenOptions}
                  onItemClick={() =>
                    navigate('/consumer/productdetails', {
                      state: { productId: pid },
                    })
                  }
                />
              </div>
            );
          })}
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="bb-scroll-nav-btn bb-scroll-nav-right"
            onClick={() => handleScroll('right')}
            aria-label="Scroll forward"
          >
            <img
              src={CaretRight}
              alt="›"
              style={{
                width: 14,
                height: 14,
              }}
            />
          </button>
        )}
      </div>

      {/* Mobile Subtle Full-Width "See All" Action Button Under Section */}
      <div className="bb-cat-bottom-action-row">
        <button
          type="button"
          className="bb-cat-mobile-see-all-btn"
          onClick={handleNavigateToCategory}
        >
          <span>See All</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </section>
  );
}

export default function FeaturedCategoriesSection({ categorySections = [] }) {
  const navigate = useNavigate();

  // 1. Reactive Customer Session
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const syncAuth = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        setCustomer(stored ? JSON.parse(stored) : null);
      } catch {
        setCustomer(null);
      }
    };

    window.addEventListener('auth-state-change', syncAuth);
    window.addEventListener('storage', syncAuth);

    return () => {
      window.removeEventListener('auth-state-change', syncAuth);
      window.removeEventListener('storage', syncAuth);
    };
  }, []);

  const customerId = customer?._id || customer?.id;

  // 2. Guest Bucket State Listener
  const [guestBucketMap, setGuestBucketMap] = useState(() =>
    guestBucketManager.getQuantityMap()
  );

  useEffect(() => {
    const handleGuestChange = () => {
      setGuestBucketMap(guestBucketManager.getQuantityMap());
    };

    window.addEventListener('guest-bucket-change', handleGuestChange);
    return () => window.removeEventListener('guest-bucket-change', handleGuestChange);
  }, []);

  // 3. Options Sheet State
  const [isOptionsSheetVisible, setIsOptionsSheetVisible] = useState(false);
  const [selectedProductWithOptions, setSelectedProductWithOptions] = useState([]);
  const [isOptionsAvailableForBuyNow, setIsOptionsAvailableForBuyNow] = useState(true);

  // 4. API Queries & Mutations
  const { data: rawAddress } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  const addItemToSavedMutation = useAddItemToSavedList();
  const removeItemFromSavedMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  const hubId =
    rawAddress?.data?.address?.hub_id ||
    rawAddress?.data?.hub_id ||
    rawAddress?.hub_id ||
    customer?.hubId ||
    customer?.hub_id;

  const savedItemsSet = useMemo(() => {
    const list = Array.isArray(savedList) ? savedList : savedList?.items || [];
    return new Set(
      list.map((item) => String(item.product_id || item.item_id || item._id))
    );
  }, [savedList]);

  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: bItem.quantity,
          bucketId:
            bucketSummary?.buy_now_bucket?.bucket_id ||
            bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  const getProductQuantity = (pid) => {
    if (customerId) {
      return bucketItemsMap.get(pid)?.quantity;
    }
    return guestBucketMap.get(pid);
  };

  const handleToggleSave = async (item, shouldSave) => {
    if (!customerId) {
      navigate('/consumer');
      return false;
    }
    const pid = String(item.product_id || item._id || item.id);
    try {
      if (shouldSave) {
        await addItemToSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      } else {
        await removeItemFromSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to toggle saved item:', err);
      return false;
    }
  };

  const handleOpenOptions = async (productItem, isBuyNowAvailable) => {
    const pid = String(productItem.product_id || productItem._id);
    try {
      const purchase_mode = isBuyNowAvailable ? 'Buy Now' : 'Quote';
      const variantsResponse = await fetchProductVariants({
        product_id: pid,
        purchase_mode,
      });
      const list = Array.isArray(variantsResponse)
        ? variantsResponse
        : variantsResponse?.product_variants || [];

      setSelectedProductWithOptions(list);
      setIsOptionsAvailableForBuyNow(isBuyNowAvailable);
      setIsOptionsSheetVisible(true);
    } catch (error) {
      console.error('Error fetching product options:', error);
    }
  };

  if (!categorySections || categorySections.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
      <style>{`
        .bb-cat-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          margin-bottom: 12px;
          box-sizing: border-box;
        }

        .bb-cat-title-cluster {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          min-width: 0;
          flex: 1;
        }

        .bb-cat-heading {
          font-family: ${theme.typography.headingMD?.fontFamily || 'sans-serif'};
          font-size: clamp(18px, 1.8vw, 20px);
          font-weight: 800;
          color: #2B2A29;
          margin: 0;
          letter-spacing: -0.2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bb-cat-see-all-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          flex-shrink: 0;
          margin-left: auto;
          padding: 4px 0 4px 12px;
          user-select: none;
        }

        .bb-cat-see-all-text {
          font-size: 13px;
          font-weight: 800;
          color: #E9001E;
          letter-spacing: 0.1px;
        }

        .bb-rail-outer-shell {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .featured-products-scroll {
          display: flex;
          overflow-x: auto;
          overflow-y: visible;
          gap: 16px;
          padding: 6px 2px 14px 2px;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          width: 100%;
          box-sizing: border-box;
          scroll-behavior: smooth;
        }

        .featured-products-scroll::-webkit-scrollbar {
          display: none;
        }

        .featured-card-wrapper {
          width: 176px;
          min-width: 176px;
          flex-shrink: 0;
          scroll-snap-align: start;
          box-sizing: border-box;
        }

        .bb-scroll-nav-btn {
          position: absolute;
          top: calc(50% - 10px);
          transform: translateY(-50%);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #FFFFFF;
          border: 1px solid #CBD5E1;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
          padding: 0;
        }

        .bb-scroll-nav-btn:hover {
          background-color: #F8FAFC;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.22);
          transform: translateY(-50%) scale(1.05);
        }

        .bb-scroll-nav-btn:active {
          transform: translateY(-50%) scale(0.95);
        }

        .bb-scroll-nav-left {
          left: -14px;
        }

        .bb-scroll-nav-right {
          right: -14px;
        }

        /* ── Mobile Subtle "See All" Action Row ── */
        .bb-cat-bottom-action-row {
          display: none;
          width: 100%;
          padding: 6px 2px 0 2px;
          box-sizing: border-box;
        }

        .bb-cat-mobile-see-all-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: #FFFFFF;
  border: 1px solid #CBD5E1; /* Increased border contrast from #E2E8F0 */
  border-radius: 12px;
  padding: 11px 16px;
  font-size: 13.5px;
  font-weight: 700;
  color: #0F172A;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
  user-select: none;
}

.bb-cat-mobile-see-all-btn:active {
  border-color: #94A3B8;
  background-color: #F8FAFC;
  transform: scale(0.99);
}

        /* ── Screen Breakpoint Logic ── */
        @media (max-width: 768px) {
          .desktop-only {
            display: none !important;
          }

          .bb-cat-bottom-action-row {
            display: block;
          }

          .bb-scroll-nav-btn {
            display: none !important;
          }

          .featured-card-wrapper {
            width: 160px;
            min-width: 160px;
          }
        }

        @media (max-width: 480px) {
          .featured-card-wrapper {
            width: 146px;
            min-width: 146px;
          }
          .featured-products-scroll {
            gap: 12px;
          }
        }
      `}</style>

      {categorySections.map((section) => {
        if (!section?.products || section.products.length === 0) return null;

        return (
          <CategoryRail
            key={section.product_category_id}
            section={section}
            customerId={customerId}
            hubId={hubId}
            bucketItemsMap={bucketItemsMap}
            savedItemsSet={savedItemsSet}
            getProductQuantity={getProductQuantity}
            handleToggleSave={handleToggleSave}
            handleOpenOptions={handleOpenOptions}
          />
        );
      })}

      <ProductOptionsModal
        visible={isOptionsSheetVisible}
        onClose={() => setIsOptionsSheetVisible(false)}
        cardTitle={isOptionsAvailableForBuyNow ? 'Add To Buy' : 'Add To Quote'}
        productData={selectedProductWithOptions}
        bucketItemsMap={bucketItemsMap}
        customerId={customerId}
        hubId={hubId}
        purchaseType={isOptionsAvailableForBuyNow ? 'BUY_NOW' : 'RFQ'}
      />
    </div>
  );
}